import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPI = [
  "Wedding planner",
  "Catering / Banqueting",
  "Location per matrimoni",
  "Organizzatore di eventi",
  "Organizzatore privato",
  "Agenzia di comunicazione / eventi",
  "Società di eventi",
  "Pro Loco / Associazione",
  "Comune / Ente pubblico",
  "Gestore di location",
  "Azienda / Hospitality",
  "Altro",
];

const AFFLUENZE = [
  "Fino a 500",
  "Da 500 a 1.000",
  "Da 1.000 a 3.000",
  "Da 3.000 a 10.000",
  "Oltre 10.000",
  "Da definire",
];

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50 mb-2.5">
    {children}
  </label>
);

export default function PartnerForm() {
  const [form, setForm] = useState({
    nome_cognome: "",
    azienda: "",
    ruolo: "",
    email: "",
    telefono: "",
    tipologia_attivita: "",
    tipologia_manifestazione: "",
    localita_provincia: "",
    data_prevista: "",
    affluenza_stimata: "",
    numero_giorni: "",
    spazio_disponibile: "",
    messaggio: "",
    website: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.nome_cognome.trim().length < 2) e.nome_cognome = "Inserisci nome e cognome";
    if (form.azienda.trim().length < 2) e.azienda = "Inserisci il nome dell'azienda o attività";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Inserisci un indirizzo email valido";
    if (form.telefono.replace(/\D/g, "").length < 8) e.telefono = "Inserisci un numero di telefono valido";
    if (!form.tipologia_attivita) e.tipologia_attivita = "Seleziona la tipologia di organizzazione";
    if (form.spazio_disponibile.trim().length < 2) e.spazio_disponibile = "Indica lo spazio disponibile";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Controlla i campi evidenziati");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/partner-requests`, form, { timeout: 15000 });
      setDone(true);
      toast.success("Richiesta partner inviata. A presto!");
    } catch (err) {
      toast.error("Invio non riuscito. Riprova più tardi.");
    } finally {
      setSending(false);
    }
  };

  if (done)
    return (
      <div
        data-testid="partner-success-panel"
        className="rounded-3xl border border-brand-orange/40 bg-brand-orange/[0.06] p-10 md:p-14 text-center"
      >
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-orange text-brand-ink">
          <Check size={26} strokeWidth={3} />
        </span>
        <h3 className="mt-6 font-display text-4xl font-semibold">Grazie!</h3>
        <p className="mt-4 text-sm leading-relaxed text-white/60 max-w-md mx-auto">
          Abbiamo ricevuto la tua richiesta di collaborazione: ti ricontatteremo per costruire
          insieme una formula adatta ai tuoi eventi.
        </p>
      </div>
    );

  return (
    <form onSubmit={submit} noValidate data-testid="partner-form" className="space-y-8">
      <div className="grid gap-8 md:grid-cols-2">
        <div>
          <Label htmlFor="p-nome">Nome e cognome *</Label>
          <input
            id="p-nome"
            data-testid="partner-field-nome"
            className={`field ${errors.nome_cognome ? "border-red-500/70" : ""}`}
            placeholder="Il tuo nome"
            value={form.nome_cognome}
            onChange={(e) => set("nome_cognome", e.target.value)}
          />
          {errors.nome_cognome && <p className="mt-2 text-xs text-red-400">{errors.nome_cognome}</p>}
        </div>
        <div>
          <Label htmlFor="p-azienda">Organizzazione o ragione sociale *</Label>
          <input
            id="p-azienda"
            data-testid="partner-field-azienda"
            className={`field ${errors.azienda ? "border-red-500/70" : ""}`}
            placeholder="Nome dell'organizzazione"
            value={form.azienda}
            onChange={(e) => set("azienda", e.target.value)}
          />
          {errors.azienda && <p className="mt-2 text-xs text-red-400">{errors.azienda}</p>}
        </div>
        <div>
          <Label htmlFor="p-ruolo">Ruolo</Label>
          <input
            id="p-ruolo"
            data-testid="partner-field-ruolo"
            className="field"
            placeholder="Es. titolare, event manager"
            value={form.ruolo}
            onChange={(e) => set("ruolo", e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="p-tipo">Tipologia di organizzazione *</Label>
          <select
            id="p-tipo"
            data-testid="partner-field-tipologia"
            className={`field appearance-none ${errors.tipologia_attivita ? "border-red-500/70" : ""} ${form.tipologia_attivita ? "" : "text-white/30"}`}
            value={form.tipologia_attivita}
            onChange={(e) => set("tipologia_attivita", e.target.value)}
          >
            <option value="">Seleziona…</option>
            {TIPI.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          {errors.tipologia_attivita && <p className="mt-2 text-xs text-red-400">{errors.tipologia_attivita}</p>}
        </div>
        <div>
          <Label htmlFor="p-manifestazione">Tipologia di manifestazione</Label>
          <input
            id="p-manifestazione"
            data-testid="partner-field-manifestazione"
            className="field"
            placeholder="Es. sagra, festival, festa di piazza…"
            value={form.tipologia_manifestazione}
            onChange={(e) => set("tipologia_manifestazione", e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="p-localita">Località e provincia</Label>
          <input
            id="p-localita"
            data-testid="partner-field-localita"
            className="field"
            placeholder="Es. Siena (SI)"
            value={form.localita_provincia}
            onChange={(e) => set("localita_provincia", e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="p-data">Data prevista</Label>
          <input
            id="p-data"
            type="date"
            data-testid="partner-field-data"
            className="field [color-scheme:dark]"
            value={form.data_prevista}
            onChange={(e) => set("data_prevista", e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="p-affluenza">Affluenza stimata</Label>
          <select
            id="p-affluenza"
            data-testid="partner-field-affluenza"
            className={`field appearance-none ${form.affluenza_stimata ? "" : "text-white/30"}`}
            value={form.affluenza_stimata}
            onChange={(e) => set("affluenza_stimata", e.target.value)}
          >
            <option value="">Seleziona…</option>
            {AFFLUENZE.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="p-giorni">Numero di giorni dell'evento</Label>
          <input
            id="p-giorni"
            type="number"
            min="1"
            max="60"
            data-testid="partner-field-giorni"
            className="field"
            placeholder="Es. 1"
            value={form.numero_giorni}
            onChange={(e) => set("numero_giorni", e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="p-spazio">Spazio disponibile *</Label>
          <input
            id="p-spazio"
            data-testid="partner-field-spazio"
            className={`field ${errors.spazio_disponibile ? "border-red-500/70" : ""}`}
            placeholder="Es. piazza 15×10 m, cortile, area bar…"
            value={form.spazio_disponibile}
            onChange={(e) => set("spazio_disponibile", e.target.value)}
          />
          {errors.spazio_disponibile && <p className="mt-2 text-xs text-red-400">{errors.spazio_disponibile}</p>}
        </div>
        <div>
          <Label htmlFor="p-email">Email *</Label>
          <input
            id="p-email"
            type="email"
            data-testid="partner-field-email"
            className={`field ${errors.email ? "border-red-500/70" : ""}`}
            placeholder="nome@esempio.it"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
          />
          {errors.email && <p className="mt-2 text-xs text-red-400">{errors.email}</p>}
        </div>
        <div>
          <Label htmlFor="p-telefono">Telefono *</Label>
          <input
            id="p-telefono"
            type="tel"
            data-testid="partner-field-telefono"
            className={`field ${errors.telefono ? "border-red-500/70" : ""}`}
            placeholder="+39 000 000 0000"
            value={form.telefono}
            onChange={(e) => set("telefono", e.target.value)}
          />
          {errors.telefono && <p className="mt-2 text-xs text-red-400">{errors.telefono}</p>}
        </div>
      </div>

      <div>
        <Label htmlFor="p-messaggio">Messaggio</Label>
        <textarea
          id="p-messaggio"
          data-testid="partner-field-messaggio"
          rows={4}
          className="field resize-none"
          placeholder="Parlaci dei tuoi eventi e di come possiamo completare la tua offerta."
          value={form.messaggio}
          onChange={(e) => set("messaggio", e.target.value)}
        />
      </div>

      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={form.website}
        onChange={(e) => set("website", e.target.value)}
        className="hidden"
      />

      <button
        type="submit"
        data-testid="partner-submit"
        disabled={sending}
        className="group inline-flex w-full md:w-auto items-center justify-center gap-3 rounded-full bg-brand-orange px-10 py-5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-ink shadow-[0_0_50px_rgba(255,121,0,0.3)] transition-colors duration-300 hover:bg-[#ff9433] disabled:opacity-60"
      >
        {sending ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Invio in corso…
          </>
        ) : (
          <>
            Richiedi una proposta di partnership
            <ArrowRight size={15} strokeWidth={2.4} className="transition-transform duration-300 group-hover:translate-x-1" />
          </>
        )}
      </button>
      <p className="text-[11px] leading-relaxed text-white/40 max-w-xl">
        I dati delle richieste vengono usati esclusivamente per rispondere alla richiesta di
        collaborazione, secondo la privacy policy del sito.
      </p>
    </form>
  );
}
